import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ErrorResponse } from '@/types/api.types';
import { BYPASS_AUTH } from '@/lib/constants';

interface UseApiOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export function useApi(options: UseApiOptions = {}) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // IMPORTANT: don't depend on the whole `options` object (it may be recreated per render)
  const { onSuccess, onError, showSuccessToast, showErrorToast } = options;

  const execute = useCallback(
    async (
      method: 'get' | 'post' | 'put' | 'patch' | 'delete',
      url: string,
      payload?: any,
      params?: Record<string, any>
    ) => {
      // Bypass API calls if flag is enabled
      if (BYPASS_AUTH) {
        console.log(`[BYPASS MODE] API call skipped: ${method.toUpperCase()} ${url}`, { payload, params });

        // Return mock data based on the endpoint
        const mockData = getMockData(url, method);
        setData(mockData);

        if (showSuccessToast) {
          toast.success('تم التنفيذ بنجاح (وضع التطوير)');
        }

        onSuccess?.(mockData);
        return mockData;
      }

      setLoading(true);
      setError(null);

      try {
        let response;

        if (method === 'get') {
          response = await api.get(url, params);
        } else if (method === 'post') {
          response = await api.post(url, payload);
        } else if (method === 'put') {
          response = await api.put(url, payload);
        } else if (method === 'patch') {
          response = await api.patch(url, payload);
        } else {
          response = await api.delete(url);
        }

        if (response.success === false) {
          throw new Error(response.message || 'حدث خطأ ما');
        }

        setData(response.data);

        if (showSuccessToast && response.message) {
          toast.success(response.message);
        }

        onSuccess?.(response.data);

        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const errorMessage = axiosError.response?.data?.message || (err as Error).message || 'حدث خطأ ما';

        setError(errorMessage);

        if (showErrorToast !== false) {
          toast.error(errorMessage);
        }

        onError?.(errorMessage);

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, onError, showSuccessToast, showErrorToast]
  );

  const get = useCallback((url: string, params?: Record<string, any>) => {
    return execute('get', url, undefined, params);
  }, [execute]);

  const post = useCallback((url: string, data?: any) => {
    return execute('post', url, data);
  }, [execute]);

  const put = useCallback((url: string, data?: any) => {
    return execute('put', url, data);
  }, [execute]);

  const patch = useCallback((url: string, data?: any) => {
    return execute('patch', url, data);
  }, [execute]);

  const del = useCallback((url: string) => {
    return execute('delete', url);
  }, [execute]);

  return { data, loading, error, get, post, put, patch, delete: del };
}

// Mock data generator for bypass mode
function getMockData(url: string, method: string) {
  // Dashboard stats
  if (url.includes('/stats')) {
    return {
      total_shipments: 150,
      pending_shipments: 25,
      delivered_shipments: 100,
      in_transit_shipments: 25,
    };
  }
  
  // Shipments list
  if (url.includes('/shipments') && method === 'get') {
    return {
      list: [],
      meta: {
        total: 0,
        page: '1',
        limit: '10',
        totalPages: 0,
      },
    };
  }
  
  // Create shipment
  if (url.includes('/shipments/create')) {
    return {
      tracking_number: 'QuBox' + Math.random().toString(36).substring(2, 10).toUpperCase(),
      message: 'تم إنشاء الشحنة بنجاح',
    };
  }
  
  // Drivers list
  if (url.includes('/drivers')) {
    return {
      data: [],
      total: 0,
    };
  }
  
  // Map stats
  if (url.includes('/map/stats')) {
    return {
      current_orders: 25,
      late_orders: 3,
      available_drivers: 8,
    };
  }
  
  // Reports
  if (url.includes('/reports')) {
    return {
      financial: {
        total_revenue: 485000,
        total_expenses: 325000,
        net_profit: 160000,
        pending_invoices: 75000,
        cod_collected: 285000,
        prepaid_amount: 200000,
      },
      management: {
        total_shipments: 1856,
        active_tenants: 42,
        total_drivers: 85,
        pending_orders: 127,
        completed_today: 234,
        cancelled_shipments: 48,
      },
      performance: {
        delivery_rate: 94.5,
        avg_delivery_time: '2.5 ساعة',
        customer_satisfaction: 4.6,
        complaints_count: 23,
        on_time_delivery: 89.3,
        failed_delivery_rate: 5.5,
      },
    };
  }
  
  // Users list
  if (url.includes('/users')) {
    return {
      list: [
        {
          id: '1',
          display_name: 'أحمد محمد',
          email: 'ahmed@delivery.sa',
          phone: '0501234567',
          role: 'admin',
          status: 'active',
          created_at: '2024-01-10',
          last_login: '2024-03-15T10:30:00',
        },
        {
          id: '2',
          display_name: 'فاطمة علي',
          email: 'fatima@delivery.sa',
          phone: '0509876543',
          role: 'manager',
          status: 'active',
          created_at: '2024-01-15',
          last_login: '2024-03-14T14:20:00',
        },
        {
          id: '3',
          display_name: 'خالد حسن',
          email: 'khaled@delivery.sa',
          phone: '0551122334',
          role: 'operator',
          status: 'active',
          created_at: '2024-02-01',
          last_login: '2024-03-15T09:15:00',
        },
        {
          id: '4',
          display_name: 'نورة سعيد',
          email: 'noura@delivery.sa',
          phone: '0544332211',
          role: 'viewer',
          status: 'inactive',
          created_at: '2024-02-10',
          last_login: '2024-03-10T16:45:00',
        },
        {
          id: '5',
          display_name: 'عمر يوسف',
          email: 'omar@delivery.sa',
          phone: '0566778899',
          role: 'operator',
          status: 'suspended',
          created_at: '2024-02-20',
          last_login: '2024-03-08T11:30:00',
        },
      ],
      meta: {
        total: 5,
        page: '1',
        limit: '10',
        totalPages: 1,
      },
    };
  }
  
  // Tenants list
  if (url.includes('/tenants')) {
    return {
      list: [
        {
          id: '1',
          name: 'شركة الشحن السريع',
          email: 'info@fastship.com',
          phone: '0501234567',
          subscription_type: 'enterprise',
          integration_type: 'api',
          status: 'active',
          created_at: '2024-01-15',
          shipments_stats: {
            new: 45,
            completed: 320,
            pending: 12,
            total: 377,
          },
        },
        {
          id: '2',
          name: 'متجر الإلكترونيات الحديثة',
          email: 'contact@moderntech.sa',
          phone: '0509876543',
          subscription_type: 'premium',
          integration_type: 'webhook',
          status: 'active',
          created_at: '2024-02-10',
          shipments_stats: {
            new: 28,
            completed: 150,
            pending: 8,
            total: 186,
          },
        },
        {
          id: '3',
          name: 'مؤسسة التجارة العامة',
          email: 'sales@trading.com',
          phone: '0551122334',
          subscription_type: 'basic',
          integration_type: 'manual',
          status: 'active',
          created_at: '2024-03-05',
          shipments_stats: {
            new: 15,
            completed: 85,
            pending: 5,
            total: 105,
          },
        },
        {
          id: '4',
          name: 'شركة الأزياء العصرية',
          email: 'info@fashion.sa',
          phone: '0544332211',
          subscription_type: 'premium',
          integration_type: 'api',
          status: 'suspended',
          created_at: '2024-01-20',
          shipments_stats: {
            new: 0,
            completed: 220,
            pending: 3,
            total: 223,
          },
        },
        {
          id: '5',
          name: 'متجر الأثاث المنزلي',
          email: 'sales@furniture.com',
          phone: '0566778899',
          subscription_type: 'enterprise',
          integration_type: 'webhook',
          status: 'active',
          created_at: '2024-02-25',
          shipments_stats: {
            new: 32,
            completed: 410,
            pending: 18,
            total: 460,
          },
        },
      ],
      meta: {
        total: 5,
        page: '1',
        limit: '10',
        totalPages: 1,
      },
    };
  }
  
  // Default empty response
  return { message: 'Success' };
}
